import {
  ECareType,
  EClaimType,
  EPatientRelationship,
  IBenefit,
  IFeatureCategory,
  IMember,
} from '@/types';
import { z } from 'zod';
import { create } from 'zustand';

export enum EClaimCreateFlowSteps {
  // Common Steps
  SELECT_CLAIM_TYPE = 'SELECT_CLAIM_TYPE',
  ORG_UNDER_ENROLLMENT = 'ORG_UNDER_ENROLLMENT',
  CLAIM_CREATED = 'CLAIM_CREATED',
  // Cashless Claim Flow Steps
  SELECT_PATIENT = 'SELECT_PATIENT',
  CLAIM_FOUND = 'CLAIM_FOUND',
  COLLECT_BASIC_INFO = 'BASIC_INFO',
  COLLECT_MORE_INFO = 'MORE_INFO',
  NETWORK_HOSPITAL_V1 = 'NETWORK_HOSPITAL_V1',
  NETWORK_HOSPITAL_V2 = 'NETWORK_HOSPITAL_V2',
  // Plum OPD Claim Flow Steps
  TREATMENT_DETAILS = 'TREATMENT_DETAILS',
}

export type IClaimClaimCreateState = {
  patientId: string | null;
  typeOfClaim: EClaimType | null;
  typeOfCare: ECareType | null;
  reasonForHospitalization: string | null;
  treatmentStage: string | null;
};

/**
 * WEB-04 (VAPT 2026): cap for the member-typed "Reason for expense" /
 * "Treatment details" text. Keep in step with base-api's
 * CLAIM_REASON_MAX_LENGTH (default 255). The server check is the real
 * control; this cap stops typing/pasting past it and explains why.
 */
export const CLAIM_REASON_MAX_LENGTH = 255;
export const CLAIM_REASON_TOO_LONG_MESSAGE = `Reason can be at most ${CLAIM_REASON_MAX_LENGTH} characters`;

export const ClaimCreateFormSchema = z.object({
  // type of claim
  typeOfClaim: z.nativeEnum(EClaimType),
  benefitCategory: z.string(),
  // select a patient
  patientId: z.string().min(1),
  patientName: z.string().min(1),
  patientRelationship: z.nativeEnum(EPatientRelationship),
  // basic information
  typeOfCare: z.nativeEnum(ECareType),
  reasonForHospitalization: z
    .string()
    .trim()
    .min(1)
    .max(CLAIM_REASON_MAX_LENGTH, CLAIM_REASON_TOO_LONG_MESSAGE),
  treatmentStage: z.string().min(1),
  // bit more information
  knowsDate: z.enum(['true', 'false']).nullish(),
  tentativeDays: z.string().optional().nullable(),
  date: z.string().min(1),
  dateFieldName: z.string().min(1),
  // insurance selection
  isOffline: z.boolean().optional(),
  insuranceProviderHandle: z.string().optional().nullable(),
  tpaName: z.string().nullable().optional().nullable(),
  organisationBenefitId: z.string().optional().nullable(),
  typeOfBenefit: z.string().optional().nullable(),
  // network hospital
  hospitalName: z.string().optional(),
  hospitalId: z.string().min(1).optional().nullable(),
  manualHospitalName: z.string().optional().nullable(),
});

export type IClaimCreateFormSchema = Partial<
  z.infer<typeof ClaimCreateFormSchema>
>;

export type IClaimCreateState = {
  currentStep: EClaimCreateFlowSteps;
  setCurrentStep: (step: EClaimCreateFlowSteps) => void;
  formState: IClaimCreateFormSchema;
  setFormState: (value: Partial<IClaimCreateFormSchema>) => void;
  memberDetails: Array<IMember>;
  setMemberDetails: (value: Array<IMember>) => void;
  currentPatientDetails: IMember | null;
  setCurrentPatientDetails: (value: IMember | null) => void;
  currentFeatureCategory: IFeatureCategory;
  setCurrentFeatureCategory: (value: IFeatureCategory | null) => void;
  allBenefits: Map<string, IBenefit>;
  setAllBenefits: (value: Map<string, IBenefit>) => void;
  clearFormState: () => void;
};

// @ts-ignore
const useClaimCreateStore = create<IClaimCreateState>((set) => {
  return {
    currentStep: EClaimCreateFlowSteps.SELECT_CLAIM_TYPE,
    formState: {
      patientId: null,
      typeOfClaim: null,
    },
    memberDetails: [],
    allBenefits: new Map(),
    currentPatientDetails: null,
    setCurrentStep: (step: EClaimCreateFlowSteps) => set({ currentStep: step }),
    setFormState: (value: IClaimClaimCreateState) => {
      // @ts-ignore
      set((state) => {
        return {
          ...state,
          formState: {
            ...state.formState,
            ...value,
          },
        };
      });
    },
    clearFormState: () =>
      set((state) => {
        return {
          ...state,
          formState: {},
        };
      }),
    setMemberDetails: (value: Array<IMember>) => {
      set((state) => {
        return { ...state, memberDetails: value };
      });
    },
    setCurrentPatientDetails: (value: IMember) => {
      set((state) => {
        return { ...state, currentPatientDetails: value };
      });
    },
    setCurrentFeatureCategory: (value: IFeatureCategory) => {
      set((state) => {
        return { ...state, currentFeatureCategory: value };
      });
    },
    setAllBenefits: (value: Map<string, IBenefit>) => {
      set((state) => {
        return { ...state, allBenefits: value };
      });
    },
  };
});

export default useClaimCreateStore;
